import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Zap, Loader2, ChevronRight, Sparkles, Users, Filter,
  Rocket, TrendingUp, Landmark, Cpu, Megaphone, AlertTriangle,
  MessageCircle, GraduationCap, Lightbulb, Scale, Shield, Target,
  User, Heart, X, Check, Info, Stethoscope, Gavel, Siren, HardHat, Briefcase
} from "lucide-react";
import { AdBanner } from "../components/AdBanner";
import { VoiceInput } from "../components/VoiceInput";
import { SEO, faqLd } from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const GROUP_ICONS = {
  "rocket": Rocket, "trending-up": TrendingUp, "landmark": Landmark,
  "users": Users, "cpu": Cpu, "megaphone": Megaphone, "alert-triangle": AlertTriangle,
  "handshake": MessageCircle, "graduation-cap": GraduationCap, "lightbulb": Lightbulb,
  "scale": Scale, "shield": Shield, "target": Target, "user": User, "heart": Heart,
  "stethoscope": Stethoscope, "gavel": Gavel, "siren": Siren, "hard-hat": HardHat,
};

const FUNC_GROUP_COLORS = {
  conflict: "#DC2626", evidence: "#2563EB", strategy: "#7C3AED",
  execution: "#059669", human: "#D97706", oversight: "#BE185D",
};

const FREE_MAX = 4;

export const HomePage = () => {
  const [question, setQuestion] = useState("");
  const [debaters, setDebaters] = useState({});
  const [groups, setGroups] = useState({});
  const [selectedDebaters, setSelectedDebaters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [view, setView] = useState("groups"); // groups | browse | search
  const [searchTerm, setSearchTerm] = useState("");
  const [filterGroup, setFilterGroup] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Pre-fill question if arriving via a follow-up-question link (e.g. /?q=...)
  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      setQuestion(q);
      // Clear the param so refreshes don't overwrite user edits
      const next = new URLSearchParams(searchParams);
      next.delete("q");
      setSearchParams(next, { replace: true });
      // Smooth scroll to the question box so user sees the prefilled text
      setTimeout(() => {
        const el = document.querySelector('[data-testid="question-input"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/roles`),
      axios.get(`${API}/debater-groups`)
    ]).then(([r, g]) => {
      setDebaters(r.data.roles || {});
      setGroups(g.data.groups || {});
    }).catch(() => {});
  }, []);

  // Auto-suggest group when question changes
  useEffect(() => {
    if (question.trim().length > 10) {
      const timer = setTimeout(() => {
        axios.post(`${API}/suggest-group`, { topic: question })
          .then(r => setSuggestions(r.data.suggestions || []))
          .catch(() => {});
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [question]);

  const filteredDebaters = useMemo(() => {
    let entries = Object.entries(debaters);
    if (searchTerm && !searchResults) {
      const term = searchTerm.toLowerCase();
      entries = entries.filter(([k, v]) =>
        v.name.toLowerCase().includes(term) || v.style.toLowerCase().includes(term)
      );
    }
    if (searchResults && searchResults.matches?.length > 0) {
      const matchKeys = searchResults.matches.map(m => m.key);
      entries = entries.filter(([k]) => matchKeys.includes(k));
    }
    if (filterGroup) {
      entries = entries.filter(([k, v]) => v.groups && v.groups.includes(filterGroup));
    }
    return entries.sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [debaters, searchTerm, filterGroup, searchResults]);

  // Backend search with multilingual synonym support
  useEffect(() => {
    if (view === "search" && searchTerm.trim().length >= 2) {
      const timer = setTimeout(() => {
        axios.post(`${API}/debaters/search`, { query: searchTerm })
          .then(r => setSearchResults(r.data))
          .catch(() => setSearchResults(null));
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults(null);
    }
  }, [searchTerm, view]);

  const requestNotification = async () => {
    if (!notifyEmail) return;
    await axios.post(`${API}/debaters/search`, { query: searchTerm, notify_if_missing: true, email: notifyEmail }).catch(() => {});
    toast.success("Je wordt geïnformeerd als deze rol wordt toegevoegd!");
    setNotifyEmail("");
  };

  const generateTopics = async () => {
    setTopicsLoading(true);
    try {
      const res = await axios.post(`${API}/topics/generate`);
      setTopics(res.data.topics || []);
    } catch { toast.error("Topics genereren mislukt"); }
    finally { setTopicsLoading(false); }
  };

  const selectGroup = (groupId) => {
    const group = groups[groupId];
    if (group) {
      setSelectedDebaters([...group.debaters_free]);
      toast.success(`${group.name} geselecteerd`);
    }
  };

  const toggleDebater = (key) => {
    setSelectedDebaters(prev => {
      if (prev.includes(key)) return prev.filter(r => r !== key);
      if (prev.length >= FREE_MAX) {
        toast.error(`Maximum ${FREE_MAX} debaters (free tier). Upgrade naar Premium voor meer.`);
        return prev;
      }
      return [...prev, key];
    });
  };

  const startDebate = async () => {
    if (!question.trim()) return toast.error("Voer een vraag in");
    if (selectedDebaters.length < 2) return toast.error("Selecteer minimaal 2 debaters");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/debate/round1`, { question, roles: selectedDebaters });
      navigate(`/discussion/${res.data.discussion_id}`);
    } catch (e) { toast.error("Debat starten mislukt"); setLoading(false); }
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Debate Engine — AI Expert Teams voor Multi-Perspectief Analyse"
        description="Stel je AI-expertteam samen. 54 experts, 16 teams, 4 LLM-providers. Multi-perspectief analyse zonder de bias van een enkele AI. Gratis te gebruiken."
        path="/"
        jsonLd={faqLd([
          { q: "Wat is Debate Engine?", a: "Debate Engine is een AI-platform dat een team van 4 tot 6+ AI-experts (GPT-4o, Claude Sonnet, Gemini, Perplexity Sonar) parallel jouw vraag laat analyseren en daarna de tegenstellingen, sterkste claims en onopgeloste spanningen synthetiseert." },
          { q: "Waarom meerdere LLMs in plaats van één?", a: "Eén AI heeft één bias. Door GPT, Claude, Gemini en Perplexity tegelijk hun unieke perspectief te laten geven krijg je een eerlijker, completer beeld — en zie je wáár ze het oneens zijn." },
          { q: "Hoeveel kost Debate Engine?", a: "Gratis: 4 debaters per sessie, met advertenties. Premium (EUR 9,99/maand): 6+ debaters, Pro Debate Protocol met Cross-Examination en Arbiter, onbeperkte geschiedenis en geen advertenties." },
          { q: "In welke talen werkt het?", a: "Nederlands en Engels. De experts antwoorden automatisch in de taal van je vraag." },
          { q: "Kan ik debatten delen?", a: "Ja — elke voltooide discussie heeft een publieke deel-URL die je op LinkedIn, X of via e-mail kunt verspreiden." },
        ])}
      />
      {/* Hero — Asymmetric layout */}
      <div className="bg-[#0a0e1a] text-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14 sm:py-18">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <p className="text-[#2d8cf0] text-xs font-bold uppercase tracking-[0.2em] mb-4">54 AI-experts. 16 teams. 4 AI-providers.</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tighter mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Stel je <span className="text-[#2d8cf0]">expert</span>team samen
              </h1>
              <p className="text-lg text-gray-400 max-w-lg leading-relaxed mb-8">
                Multi-perspectief analyse van de beste AI-denkers. Stel een vraag, kies je team, ontvang diepgaande analyse.
              </p>
              {/* Quick-start example questions */}
              <div className="flex flex-wrap gap-2">
                {[
                  "Moet ik een eigen bedrijf starten?",
                  "Is AI een bedreiging voor banen?",
                  "Hoe maak ik betere beslissingen?",
                ].map((q, i) => (
                  <button key={q} onClick={() => { setQuestion(q); selectGroup(["startup", "creative", "personal_development"][i]); }}
                    className="px-3 py-1.5 bg-white/10 text-sm text-gray-300 hover:bg-[#2d8cf0] hover:text-white transition-all border border-white/10 hover:border-[#2d8cf0]"
                    data-testid={`quick-question-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 hidden lg:flex justify-center">
              <img src="/logo-small.png" alt="" className="h-40 w-40 opacity-20" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 -mt-8">
        {/* Onboarding Steps — eigen witte pill-card zodat tekst gegarandeerd WCAG-contrast heeft op zowel hero (donker) als content (wit) */}
        <div className="flex justify-center mb-6 mt-2" data-testid="onboarding-steps">
          <div className="inline-flex items-center gap-3 sm:gap-6 bg-white border border-[#E5E5E5] shadow-[0_4px_14px_rgb(0,0,0,0.08)] px-4 sm:px-6 py-2.5 rounded-full">
            {[
              { step: 1, label: "Stel een vraag", done: question.length > 0 },
              { step: 2, label: "Kies je team", done: selectedDebaters.length >= 2 },
              { step: 3, label: "Start het debat", done: false },
            ].map((s, i) => (
              <div key={s.step} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                  s.done ? "bg-[#2d8cf0] text-white" : "bg-gray-100 border border-[#D4D4D8] text-[#27272A]"
                }`}>{s.done ? <Check className="w-3 h-3" /> : s.step}</div>
                <span className={`text-xs font-medium ${s.done ? "text-[#2d8cf0] font-bold" : "text-[#27272A]"}`}>{s.label}</span>
                {i < 2 && <div className="w-4 sm:w-8 h-px bg-[#D4D4D8]" />}
              </div>
            ))}
          </div>
        </div>

        {/* Question Input — Elevated card */}
        <div className="bg-white border border-[#E5E5E5] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.08)]" data-testid="question-input-section">
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-3 block">Stel je vraag</label>
          <div className="flex gap-2">
            <input
              value={question} onChange={e => setQuestion(e.target.value)}
              placeholder="Wat wil je onderzoeken?"
              className="flex-1 bg-[#FAFAFA] border border-[#E5E5E5] px-4 py-3.5 text-sm focus:border-[#2d8cf0] focus:outline-none focus:ring-2 focus:ring-[#2d8cf0]/20 transition-all"
              onKeyDown={e => e.key === "Enter" && startDebate()}
              data-testid="question-input"
            />
            <VoiceInput onTranscript={(text) => setQuestion(prev => prev ? prev + " " + text : text)} />
            <button onClick={startDebate} disabled={loading || selectedDebaters.length < 2}
              className="bg-[#2d8cf0] text-white px-8 py-3.5 font-bold text-sm hover:bg-[#1a6fd4] transition-all disabled:opacity-40 flex items-center gap-2 active:scale-95"
              data-testid="start-debate-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              START
            </button>
          </div>
          {/* Selected debaters */}
          {selectedDebaters.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#52525B]">Team ({selectedDebaters.length}/{FREE_MAX}):</span>
              {selectedDebaters.map(id => (
                <span key={id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#0a0e1a] text-white text-xs font-medium rounded-md">
                  {debaters[id]?.name || id}
                  <button onClick={() => toggleDebater(id)} className="hover:text-red-400 ml-0.5"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* AI Suggestions */}
        {suggestions.length > 0 && (
          <div className="mt-4 bg-[#2d8cf0]/5 border border-[#2d8cf0]/20 p-4" data-testid="suggestions-panel">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#2d8cf0] mb-3">Aanbevolen teams:</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map(s => {
                const Icon = GROUP_ICONS[groups[s.group_id]?.icon] || Users;
                return (
                  <button key={s.group_id} onClick={() => selectGroup(s.group_id)}
                    className="px-3 py-2 bg-white border border-[#2d8cf0]/30 text-sm font-medium hover:border-[#2d8cf0] hover:-translate-y-0.5 hover:shadow-md transition-all flex items-center gap-2"
                    data-testid={`suggestion-${s.group_id}`}
                  >
                    <Icon className="w-4 h-4 text-[#2d8cf0]" />
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Ad Position 1: Between question input and topic generator */}
      <AdBanner variant="between" className="mt-4" />

      {/* Topics */}
        <div className="mt-6 mb-6">
          <button onClick={generateTopics} disabled={topicsLoading}
            className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] hover:text-[#2d8cf0] flex items-center gap-1.5 transition-colors"
            data-testid="generate-topics-btn"
          >
            {topicsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Genereer onderwerpen
          </button>
          {topics.length > 0 && (
            <div className="mt-3 space-y-2">
              {topics.map((t, i) => (
                <button key={t} onClick={() => setQuestion(t)}
                  className="block w-full text-left bg-white border border-[#E5E5E5] px-4 py-3 text-sm hover:border-[#2d8cf0] hover:-translate-y-0.5 hover:shadow-md transition-all"
                  data-testid={`topic-${i}`}
                >
                  <ChevronRight className="w-3 h-3 inline mr-2 text-[#2d8cf0]" />{t}
                </button>
              ))}
            </div>
          )}
        </div>

      {/* Selection Mode Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#E5E5E5] pb-3" data-testid="selection-tabs">
        {[
          { id: "groups", label: "Quick Start", icon: Users },
          { id: "browse", label: "Bladeren A-Z", icon: Filter },
          { id: "search", label: "Zoeken", icon: Search },
        ].map(tab => (
          <button key={tab.id} onClick={() => setView(tab.id)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all rounded-md ${
              view === tab.id ? "bg-[#0a0e1a] text-white" : "bg-white text-[#52525B] hover:bg-[#FAFAFA] border border-[#E5E5E5]"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />{tab.label}
          </button>
        ))}
      </div>

      {/* QUICK START — Groups View */}
      {view === "groups" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="groups-grid">
          {Object.entries(groups).map(([gid, group]) => {
            const Icon = GROUP_ICONS[group.icon] || Users;
            const isSelected = group.debaters_free.every(d => selectedDebaters.includes(d));
            return (
              <button key={gid} onClick={() => selectGroup(gid)}
                className={`text-left p-5 border transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] ${
                  isSelected ? "border-[#2d8cf0] bg-[#2d8cf0]/5 shadow-md" : "border-[#E5E5E5] bg-white hover:border-[#2d8cf0]/50"
                }`}
                data-testid={`group-${gid}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#0a0e1a] flex items-center justify-center flex-shrink-0 rounded-md">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-bold text-sm leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{group.name}</p>
                      {group.category === "b2b" && (
                        <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#0a0e1a] text-white rounded-sm" title="Professioneel team — alleen indicatief, vervangt geen vakprofessional">
                          <Briefcase className="w-2 h-2" />B2B
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-[#52525B] mt-1 line-clamp-2">{group.description}</p>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {group.debaters_free.map(d => (
                        <span key={d} className="text-[9px] font-medium bg-[#FAFAFA] text-[#52525B] px-1.5 py-0.5 border border-[#E5E5E5] rounded-sm">{debaters[d]?.name || d}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {isSelected && <div className="mt-3 text-[10px] font-bold text-[#2d8cf0] flex items-center gap-1"><Check className="w-3 h-3" />Geselecteerd</div>}
              </button>
            );
          })}
        </div>
      )}

      {/* BROWSE — Alphabetical with filters */}
      {view === "browse" && (
        <div data-testid="browse-view">
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={() => setFilterGroup(null)}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase border rounded-md transition-all ${!filterGroup ? "bg-[#0a0e1a] text-white border-[#0a0e1a]" : "border-[#E5E5E5] hover:border-gray-400"}`}
            >Alle</button>
            {Object.entries(FUNC_GROUP_COLORS).map(([gid, color]) => (
              <button key={gid} onClick={() => setFilterGroup(gid)}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase border rounded-md transition-all ${filterGroup === gid ? "text-white" : "border-[#E5E5E5] hover:border-gray-400"}`}
                style={filterGroup === gid ? { backgroundColor: color, borderColor: color } : {}}
              >{gid}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filteredDebaters.map(([key, d]) => (
              <button key={key} onClick={() => toggleDebater(key)}
                className={`px-4 py-3 text-xs text-left border transition-all duration-200 hover:-translate-y-0.5 ${
                  selectedDebaters.includes(key) ? "border-[#2d8cf0] bg-[#0a0e1a] text-white" : "border-[#E5E5E5] bg-white hover:border-[#2d8cf0]/50 hover:shadow-md"
                }`}
                data-testid={`debater-${key}`}
              >
                <div className="flex items-start justify-between gap-1">
                  <span className="font-bold block text-[12px]">{d.name}</span>
                  {d.professional_anchor && (
                    <span title="Specialist met vakspecifieke positie" className="text-[8px] font-bold px-1 py-0.5 bg-amber-500 text-white rounded-sm" data-testid="specialist-badge">
                      SPEC
                    </span>
                  )}
                </div>
                <span className="text-[10px] opacity-60 block mt-1 line-clamp-1">{d.style}</span>
                <div className="flex gap-1 mt-2">
                  {d.groups?.map(g => (
                    <span key={g} className="w-2 h-2 rounded-full" style={{ backgroundColor: FUNC_GROUP_COLORS[g] || "#999" }} />
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* SEARCH View */}
      {view === "search" && (
        <div data-testid="search-view">
          <div className="relative mb-4">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
            <input
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Zoek een debater (elke taal)..."
              className="w-full pl-10 pr-4 py-3.5 bg-white border border-[#E5E5E5] text-sm focus:border-[#2d8cf0] focus:outline-none focus:ring-2 focus:ring-[#2d8cf0]/20 transition-all"
              autoFocus
              data-testid="debater-search-input"
            />
          </div>

          {/* Not found — proactive suggestions */}
          {searchResults?.not_found && searchTerm.length >= 2 && (
            <div className="mb-4 bg-[#f59e0b]/5 border border-[#f59e0b]/30 p-4" data-testid="search-not-found">
              <p className="text-sm font-medium text-[#92400e] mb-2">
                Geen exacte match voor &quot;{searchTerm}&quot;
              </p>
              {searchResults.suggestions?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-[#52525B] mb-2">Mogelijk relevante experts:</p>
                  <div className="flex flex-wrap gap-2">
                    {searchResults.suggestions.map(s => (
                      <button key={s.key} onClick={() => toggleDebater(s.key)}
                        className="px-3 py-1.5 bg-white border border-[#E5E5E5] text-xs font-medium hover:border-[#2d8cf0] transition-all"
                      >
                        {s.name} <span className="text-[10px] text-gray-400 ml-1">({s.reason})</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-[#f59e0b]/20 pt-3 mt-3">
                <p className="text-xs text-[#52525B] mb-2">Wil je geïnformeerd worden als deze rol wordt toegevoegd?</p>
                <div className="flex gap-2">
                  <input value={notifyEmail} onChange={e => setNotifyEmail(e.target.value)}
                    placeholder="Je e-mailadres" type="email"
                    className="flex-1 text-xs border border-[#E5E5E5] px-3 py-2 focus:outline-none focus:border-[#2d8cf0]"
                  />
                  <button onClick={requestNotification} disabled={!notifyEmail}
                    className="px-3 py-2 bg-[#2d8cf0] text-white text-xs font-bold hover:bg-[#1a6fd4] disabled:opacity-40"
                    data-testid="notify-btn"
                  >
                    Informeer mij
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filteredDebaters.map(([key, d]) => (
              <button key={key} onClick={() => toggleDebater(key)}
                className={`px-4 py-3 text-xs text-left border transition-all duration-200 hover:-translate-y-0.5 ${
                  selectedDebaters.includes(key) ? "border-[#2d8cf0] bg-[#0a0e1a] text-white" : "border-[#E5E5E5] bg-white hover:border-[#2d8cf0]/50 hover:shadow-md"
                }`}
                data-testid={`debater-${key}`}
              >
                <span className="font-bold block text-[12px]">{d.name}</span>
                <span className="text-[10px] opacity-60 block mt-1 line-clamp-1">{d.style}</span>
                <div className="flex gap-1 mt-2">
                  {d.groups?.map(g => (
                    <span key={g} className="w-2 h-2 rounded-full" style={{ backgroundColor: FUNC_GROUP_COLORS[g] || "#999" }} />
                  ))}
                </div>
              </button>
            ))}
            {filteredDebaters.length === 0 && !searchResults?.not_found && searchTerm.length >= 2 && <p className="col-span-full text-center text-[#52525B] text-sm py-8">Zoeken...</p>}
          </div>
        </div>
      )}

      {/* Composition Info */}
      {selectedDebaters.length >= 2 && (
        <div className="mt-6 bg-[#2d8cf0]/5 border border-[#2d8cf0]/20 px-4 py-3 flex items-center gap-3" data-testid="composition-info">
          <Info className="w-4 h-4 text-[#2d8cf0] flex-shrink-0" />
          <span className="text-xs text-[#111111]">
            {selectedDebaters.length} debaters geselecteerd.
            {selectedDebaters.length < FREE_MAX && ` Je kunt nog ${FREE_MAX - selectedDebaters.length} toevoegen.`}
          </span>
        </div>
      )}

      {/* Ad Position 2: After team selection */}
      <AdBanner variant="inline" className="mt-8" />

      {/* Ad Position 3: Hero-style premium CTA at bottom */}
      <AdBanner variant="hero" className="mt-6 mb-8" />
      </div>
    </div>
  );
};
